import type { CSSProperties } from 'react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CalendarEvent,
  CalendarIntegration,
  NoticeRoomTemplateLayer,
} from '@cornerstone/shared';
import { ROOM_TEMPLATE_CARD_LAYOUTS } from '@cornerstone/shared';

const DESIGN_WIDTH = 1920;
const DESIGN_HEIGHT = 1080;
const SEOUL_OFFSET_MS = 9 * 60 * 60 * 1000;

export type RoomTemplateProps = {
  layer: NoticeRoomTemplateLayer;
  room: CalendarIntegration | undefined;
  events: CalendarEvent[];
  nowMs: number;
  canvasWidth?: number;
  canvasHeight?: number;
};

export function RoomTemplate({
  layer,
  events,
  nowMs,
  canvasWidth = DESIGN_WIDTH,
  canvasHeight = DESIGN_HEIGHT,
}: RoomTemplateProps): JSX.Element {
  const layout = ROOM_TEMPLATE_CARD_LAYOUTS[layer.theme];
  const picked = useMemo(
    () => pickTemplateEvents(events, nowMs, layout.maxCards),
    [events, nowMs, layout.maxCards],
  );
  const scaleX = canvasWidth / DESIGN_WIDTH;
  const scaleY = canvasHeight / DESIGN_HEIGHT;
  const style: CSSProperties = {
    position: 'relative',
    width: canvasWidth,
    height: canvasHeight,
    overflow: 'hidden',
    background: 'transparent',
    color: layout.titleText,
    fontFamily: 'SUIT, PretendardVariable, Inter Tight, system-ui, sans-serif',
  };

  return (
    <div style={style}>
      {picked.map((event, idx) => {
        const top = (layout.y + idx * (layout.height + layout.gap)) * scaleY;
        const borderWidth = layout.borderWidth * scaleY;
        return (
          <Fragment key={event.id}>
            <div
              style={{
                position: 'absolute',
                left: layout.x * scaleX + borderWidth,
                top: top + borderWidth,
                width: layout.width * scaleX,
                height: layout.height * scaleY,
                background: layout.borderColor,
                borderRadius: layout.radius * scaleY,
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: layout.x * scaleX,
                top,
                width: layout.width * scaleX,
                height: layout.height * scaleY,
                display: 'flex',
                overflow: 'hidden',
                background: layout.cardBg,
                border: `${borderWidth}px solid ${layout.borderColor}`,
                borderRadius: layout.radius * scaleY,
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  width: layout.timeWidth * scaleX,
                  height: '100%',
                  flex: '0 0 auto',
                  background: layout.timeBg,
                  color: layout.timeText,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  whiteSpace: 'pre-line',
                  textAlign: 'center',
                  fontSize: layout.timeFontSize * scaleY,
                  fontWeight: layout.timeFontWeight,
                  lineHeight: 1.15,
                  // Round only the left corners; right edge stays square so it
                  // meets the title area with a flush vertical line.
                  borderRadius: `${layout.radius * scaleY}px 0 0 ${layout.radius * scaleY}px`,
                }}
              >
                {formatTimeRange(event, layer.theme)}
              </div>
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: layout.titleOffsetX * scaleX,
                  paddingRight: 24 * scaleX,
                  color: layout.titleText,
                  fontSize: layout.titleFontSize * scaleY,
                  fontWeight: layout.titleFontWeight,
                  lineHeight: 1.2,
                  whiteSpace: 'pre-wrap',
                  overflow: 'hidden',
                }}
              >
                {event.summary || '(제목 없음)'}
              </div>
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}

export type RoomTemplateScaledBoxProps = RoomTemplateProps & {
  style?: CSSProperties;
};

export function RoomTemplateScaledBox({
  canvasWidth = DESIGN_WIDTH,
  canvasHeight = DESIGN_HEIGHT,
  style,
  ...props
}: RoomTemplateScaledBoxProps): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const rect = el.getBoundingClientRect();
      setSize({ w: rect.width, h: rect.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scale = Math.min(size.w / canvasWidth || 1, size.h / canvasHeight || 1);
  return (
    <div ref={ref} style={{ position: 'relative', overflow: 'hidden', ...style }}>
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: canvasWidth,
          height: canvasHeight,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <RoomTemplate {...props} canvasWidth={canvasWidth} canvasHeight={canvasHeight} />
      </div>
    </div>
  );
}

export function dummyRoomTemplateEvents(nowMs: number, integrationId = 'dummy-room'): CalendarEvent[] {
  const start = seoulDayStart(nowMs) + 14 * 60 * 60 * 1000;
  const titles = [
    '[SCP] Single ID ADM 정기 스크럼\n(CX 2차 리뷰) ID 데일리 스크럼',
    '[슈퍼무브] 주간회의',
    '[이즐 통합앱] 개발 미팅',
    '[현대오토에버DCMS] 주간미팅',
    '[SCP] Single ID 디자인시스템 및\nFigSA 브리핑',
  ];
  return titles.map((summary, idx) => {
    const s = start + idx * 90 * 60 * 1000;
    const e = s + 60 * 60 * 1000;
    return {
      id: `${integrationId}:dummy-${idx}`,
      integrationId,
      calendarId: integrationId,
      googleEventId: `dummy-${idx}`,
      summary,
      startAt: s,
      endAt: e,
      startIso: new Date(s).toISOString(),
      endIso: new Date(e).toISOString(),
      organizerEmail: null,
      status: 'confirmed',
      updatedAt: new Date(nowMs).toISOString(),
    };
  });
}

function pickTemplateEvents(events: CalendarEvent[], nowMs: number, maxCards: number): CalendarEvent[] {
  const startMs = seoulDayStart(nowMs);
  const endMs = startMs + 86_400_000;
  return events
    .filter((e) => e.status !== 'cancelled')
    .filter((e) => e.startAt < endMs && e.endAt > startMs)
    .sort((a, b) => a.startAt - b.startAt)
    .slice(0, maxCards);
}

function seoulDayStart(nowMs: number): number {
  const seoulNow = nowMs + SEOUL_OFFSET_MS;
  return Math.floor(seoulNow / 86_400_000) * 86_400_000 - SEOUL_OFFSET_MS;
}

function formatTimeRange(event: CalendarEvent, theme: string): string {
  if (theme === 'hanoi') return `${formatAmpm(event.startAt)} - ${formatAmpm(event.endAt)}`;
  return `${formatHm(event.startAt)} - ${formatHm(event.endAt)}`;
}

function formatHm(ms: number): string {
  const seoul = new Date(ms + SEOUL_OFFSET_MS);
  const h = String(seoul.getUTCHours()).padStart(2, '0');
  const m = String(seoul.getUTCMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function formatAmpm(ms: number): string {
  const seoul = new Date(ms + SEOUL_OFFSET_MS);
  const hour = seoul.getUTCHours();
  const suffix = hour < 12 ? 'AM' : 'PM';
  const h = hour % 12 || 12;
  return `${h}${suffix}`;
}
